import {PhoneBlackIcon} from '@/components/svg/PhoneBlackIcon';
import {useRepresentants, useSocieteCommerciale} from '@/haligator-factory';
import {
  Box,
  Collapse,
  HStack,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react';
import {ChevronDownIcon, ChevronUpIcon} from '@chakra-ui/icons';
import {useHalState, useHalStates} from '@synako/haligator';
import {formaterTelephone} from 'ff-common';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';

/**
 * Ligne compacte : rôle + nom + appel téléphone (sans email).
 * Sur ordinateur, le numéro est aussi affiché : `tel:` n'y lance un appel que si un
 * logiciel de téléphonie est associé, le client doit donc pouvoir lire le numéro.
 */
const ContactTelephoneLigne: React.FC<{
  role: string;
  nom: string;
  telephone: string;
}> = ({role, nom, telephone}) => {
  const {t} = useTranslation();
  const telHref = `tel:${telephone.replace(/\s/g, '')}`;

  return (
    <HStack
      justify='space-between'
      align='center'
      w='100%'
      py={1.5}
      spacing={2}
    >
      <VStack
        align='flex-start'
        spacing={0}
        flex={1}
        minW={0}
      >
        <Text
          fontSize='xs'
          fontWeight='700'
          color='blue.main'
        >
          {role}
        </Text>
        <Text
          fontSize='sm'
          fontWeight='500'
          color='black.text'
          noOfLines={1}
        >
          {nom}
        </Text>
        <Text
          display={{base: 'none', md: 'block'}}
          fontSize='xs'
          color='gray.dark'
        >
          {formaterTelephone(telephone)}
        </Text>
      </VStack>
      <IconButton
        as='a'
        href={telHref}
        aria-label={t('client.conversations.appeler', {nom})}
        icon={<PhoneBlackIcon />}
        variant='ghost'
        size='sm'
        borderRadius='full'
        flexShrink={0}
        _hover={{bg: 'gray.bg'}}
      />
    </HStack>
  );
};

/**
 * Tiroir « Mes contacts » dans la popin conversations (interface client uniquement).
 * Liste compacte Commercial / Télévente / Filiale avec appel téléphone.
 */
export const ConversationsContactsTiroir: React.FC = () => {
  const {t} = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const societeCommerciale = useHalState(useSocieteCommerciale());
  const {states: representantsStates} = useHalStates(
    useRepresentants().followAll('items'),
  );

  // Commercial et télévente qui ont un numéro : le flatMap écarte les autres et garde un
  // `telephone` typé string, sans assertion.
  const representants = (representantsStates ?? [])
    .flatMap((state) => {
      const {en_tant_que, individu} = state.data.tolede;
      if (
        (en_tant_que !== 'commercial' && en_tant_que !== 'televente') ||
        !individu.telephone
      ) {
        return [];
      }
      return [
        {
          id: state.data.id,
          en_tant_que,
          nom: `${individu.prenom ?? ''} ${individu.nom ?? ''}`.trim(),
          telephone: individu.telephone,
        },
      ];
    })
    // Commercial puis télévente
    .sort((a, b) => {
      if (a.en_tant_que === b.en_tant_que) {
        return 0;
      }
      return a.en_tant_que === 'commercial' ? -1 : 1;
    });

  // Aucun numéro à appeler (ou données pas encore chargées) : pas de tiroir, plutôt qu'un
  // accordéon qui s'ouvrirait sur un cadre vide.
  if (representants.length === 0 && !societeCommerciale.data?.telephone) {
    return null;
  }

  return (
    <Box
      mt={3}
      mb={1}
      borderWidth='1px'
      borderColor='gray.light'
      borderRadius='8px'
      overflow='hidden'
    >
      {/* Aligné sur ConversationClientLine : avatar sm (32px) + ml={1} + spacing={3} */}
      <HStack
        as='button'
        type='button'
        w='100%'
        spacing={3}
        p={1}
        align='center'
        bg='blue.sky_light'
        cursor='pointer'
        onClick={() => {
          setIsOpen((prev) => !prev);
        }}
        _hover={{bg: 'blue.sky_light'}}
      >
        {/* Picto tél. dans une case de la taille de l’avatar (rond B) */}
        <Box
          w='32px'
          h='32px'
          ml={1}
          display='flex'
          alignItems='center'
          justifyContent='center'
          flexShrink={0}
        >
          <PhoneBlackIcon />
        </Box>
        <Text
          fontSize='sm'
          fontWeight='600'
          color='blue.main'
          flex={1}
          textAlign='left'
        >
          {t('menu.contacts')}
        </Text>
        {isOpen ? (
          <ChevronUpIcon
            boxSize={5}
            color='blue.main'
          />
        ) : (
          <ChevronDownIcon
            boxSize={5}
            color='blue.main'
          />
        )}
      </HStack>

      <Collapse in={isOpen}>
        <VStack
          align='stretch'
          spacing={0}
          px={3}
          py={2}
          divider={
            <Box
              borderBottom='1px solid'
              borderColor='gray.light'
            />
          }
        >
          {representants.map((contact) => (
            <ContactTelephoneLigne
              key={contact.id}
              role={t(`contact.${contact.en_tant_que}`)}
              nom={contact.nom}
              telephone={contact.telephone}
            />
          ))}

          {societeCommerciale.data?.telephone ? (
            <ContactTelephoneLigne
              role={t('contact.societe_commerciale')}
              nom={societeCommerciale.data.tolede.nom_commercial}
              telephone={societeCommerciale.data.telephone}
            />
          ) : null}
        </VStack>
      </Collapse>
    </Box>
  );
};
